import React, { memo, useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Typography,
  Stack,
  Chip,
  IconButton,
  Collapse,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { useSelector } from "react-redux";
import configServ from "../../services/config";
import useDeviceDetect from "../../Components/Detect/useDeviceDetect ";
import LoaderNew from "../../Components/Loader/LoaderNew";

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const fmtDate = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const InventoryTransformationTable = ({ isChanged, pagination = true }) => {
  const { company_id } = useSelector((state) => state.GlobalVariables);
  const rowsPerPageOptions = [20, 50, 100, 200];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1]);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // { rowIdx: true }
  const { isMobile } = useDeviceDetect();

  const fetchList = async () => {
    try {
      const res = await configServ.getManuProducts({ company_id });
      if (res.success === true) {
        setProducts(res.products || []);
      } else {
        setProducts([]);
      }
    } catch (e) {
      console.log(e);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [isChanged]);

  // The endpoint does not paginate or search — do it client-side.
  const filtered = search
    ? products.filter((p) =>
        (p.productName || "").toLowerCase().includes(search.toLowerCase())
      )
    : products;
  const count = filtered.length;
  const paginated = filtered.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const toggleExpand = (idx) =>
    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }));

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  const handleSearch = (event) => {
    setSearch(event.target.value || "");
    setPage(0);
  };

  const headers = [
    "PRODUCT",
    "QTY",
    "UNIT COST",
    "SHIFT",
    "REJECTED",
    "INPUT COST",
    "DATE",
    "ACTION",
  ];

  const headerCellStyle = {
    fontSize: 14,
    fontWeight: "bold",
    backgroundColor: "#4CB5F5",
    color: "white",
  };
  const cellStyle = { py: 0.5 };
  const evenRowStyle = { background: "#f5f5f5" };
  const oddRowStyle = { background: "white" };
  const rowStyle = { "&:hover": { background: "#f1f9fe" } };

  if (loading) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <LoaderNew />
      </Box>
    );
  }

  return (
    <>
      <Grid
        container
        alignItems="flex-end"
        justifyContent="flex-start"
        sx={{ pt: 1 }}
        spacing={1}
      >
        <Grid item xs={12} md={8}>
          <Typography sx={{ fontSize: "18px", fontWeight: "bold" }}>
            Manufactured Products: {count}
          </Typography>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            id="search"
            size="small"
            fullWidth
            placeholder="search by product name"
            variant="outlined"
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fill: "grey" }} />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
      </Grid>

      {isMobile ? (
        <>
          <Grid container spacing={2} mt={0.5}>
            {paginated.length > 0 ? (
              paginated.map((row, idx) => (
                <Grid item xs={12} key={idx}>
                  <Paper elevation={3} sx={{ p: 2 }}>
                    <Stack spacing={0.75}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Box sx={{ fontWeight: "bold" }}>
                          {page * rowsPerPage + idx + 1}.{" "}
                          {row.productName || "—"}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(idx)}
                        >
                          {expanded[idx] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={`Qty: ${row.quantity || 0}${
                            row.unit ? " " + row.unit : ""
                          }`}
                        />
                        <Chip
                          size="small"
                          label={`Rate: ₹${fmtMoney(row.newcostprice)}`}
                          color="primary"
                          variant="outlined"
                        />
                        {row.rejected_products ? (
                          <Chip
                            size="small"
                            color="warning"
                            label={`Rejected: ${row.rejected_products}`}
                          />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        Shift: {row.shift ?? "—"} · Input Cost: ₹
                        {fmtMoney(row.totalInputCost)} ·{" "}
                        {fmtDate(row.created_at)}
                      </Typography>
                      <Collapse
                        in={!!expanded[idx]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box
                          mt={1}
                          p={1}
                          sx={{ background: "#fafbfc", borderRadius: 1 }}
                        >
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ mb: 1 }}
                          >
                            Inputs ({row.inputs?.length || 0})
                          </Typography>
                          {row.inputs && row.inputs.length > 0 ? (
                            <Stack spacing={0.5}>
                              {row.inputs.map((input, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    fontSize: 12,
                                    borderBottom: "1px dashed #ddd",
                                    pb: 0.5,
                                  }}
                                >
                                  <strong>{input.itemName}</strong> —{" "}
                                  {input.quantity} {input.unit} × ₹
                                  {fmtMoney(input.unitPrice)} = ₹
                                  {fmtMoney(input.itemtotalprice)}
                                </Box>
                              ))}
                            </Stack>
                          ) : (
                            <Typography variant="caption">
                              No inputs recorded.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </Stack>
                  </Paper>
                </Grid>
              ))
            ) : (
              <Grid item xs={12} textAlign="center">
                <Box sx={{ color: "red", fontWeight: 600, py: 2 }}>
                  No data found.
                </Box>
              </Grid>
            )}
          </Grid>
          {pagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      ) : (
        <TableContainer component={Paper} sx={{ py: 1, mt: 1 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headerCellStyle}>S.No.</TableCell>
                {headers.map((h, i) => (
                  <TableCell key={i} sx={headerCellStyle}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {paginated.map((row, idx) => (
                <React.Fragment key={idx}>
                  <TableRow
                    sx={{
                      ...rowStyle,
                      ...(idx % 2 === 0 ? evenRowStyle : oddRowStyle),
                    }}
                  >
                    <TableCell sx={cellStyle}>
                      {page * rowsPerPage + idx + 1}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      <Typography variant="body2" fontWeight={600}>
                        {row.productName || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {row.quantity || 0}
                      {row.unit && (
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{ color: "text.secondary", ml: 0.5 }}
                        >
                          {row.unit}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      ₹{fmtMoney(row.newcostprice)}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {row.shift != null ? row.shift : "—"}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {row.rejected_products ? (
                        <Chip
                          size="small"
                          label={row.rejected_products}
                          color="warning"
                          variant="outlined"
                        />
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      ₹{fmtMoney(row.totalInputCost)}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      {fmtDate(row.created_at)}
                    </TableCell>
                    <TableCell sx={cellStyle}>
                      <Tooltip
                        title={expanded[idx] ? "Collapse" : "Show inputs"}
                        arrow
                      >
                        <IconButton
                          size="small"
                          onClick={() => toggleExpand(idx)}
                        >
                          {expanded[idx] ? (
                            <KeyboardArrowUpIcon />
                          ) : (
                            <KeyboardArrowDownIcon />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      sx={{
                        p: 0,
                        borderBottom: expanded[idx] ? undefined : "none",
                      }}
                      colSpan={headers.length + 1}
                    >
                      <Collapse
                        in={!!expanded[idx]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box sx={{ p: 2, background: "#f8fafc" }}>
                          <Typography
                            variant="subtitle2"
                            fontWeight={700}
                            sx={{ color: "#1e61b4", mb: 1 }}
                          >
                            Manufacturing Inputs ({row.inputs?.length || 0})
                          </Typography>
                          {row.inputs && row.inputs.length > 0 ? (
                            <TableContainer
                              component={Paper}
                              variant="outlined"
                              sx={{ background: "white" }}
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Item
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 700 }}
                                      align="right"
                                    >
                                      Quantity
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>
                                      Unit
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 700 }}
                                      align="right"
                                    >
                                      Unit Price
                                    </TableCell>
                                    <TableCell
                                      sx={{ fontWeight: 700 }}
                                      align="right"
                                    >
                                      Total
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {row.inputs.map((input, i) => (
                                    <TableRow key={i}>
                                      <TableCell>{input.itemName}</TableCell>
                                      <TableCell align="right">
                                        {input.quantity}
                                      </TableCell>
                                      <TableCell>{input.unit}</TableCell>
                                      <TableCell align="right">
                                        ₹{fmtMoney(input.unitPrice)}
                                      </TableCell>
                                      <TableCell
                                        align="right"
                                        sx={{
                                          fontWeight: 700,
                                          color: "#1e61b4",
                                        }}
                                      >
                                        ₹{fmtMoney(input.itemtotalprice)}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              No inputs recorded.
                            </Typography>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
              {paginated.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={headers.length + 1}
                    align="center"
                    sx={{ color: "red", fontWeight: 600, py: 2 }}
                  >
                    No data found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          {pagination && (
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={count}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </TableContainer>
      )}
    </>
  );
};

export default memo(InventoryTransformationTable);
